import { FieldWrapper } from "@progress/kendo-react-form";
import { TextArea, TextAreaProps } from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import * as React from "react";
import FormLabel from "./FormLabel";
import { useControlledField, type FieldValidator } from "./useControlledField";
import "./style.scss";

export interface FormTextAreaProps
  extends Omit<TextAreaProps, "name" | "value" | "onChange" | "valid"> {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
}

export const FormTextArea: React.FC<FormTextAreaProps> = ({
  name,
  validator,
  label,
  hint,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
  ...textAreaProps
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const inputId = name;
  const hintId = showHint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";

  return (
    <FieldWrapper>
      <FormLabel
        id={inputId}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={
          field.value != null && String(field.value).length > 0
            ? String(field.value)
            : undefined
        }
        placeholder={placeholder}
        optional={optional}
      >
        <div className="k-form-field-wrap">
          <TextArea
            {...textAreaProps}
            id={inputId}
            name={name}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            ariaDescribedBy={`${hintId} ${errorId}`}
            valid={!fieldState.error}
            value={(field.value as string | undefined) ?? ""}
            onChange={e => field.onChange(e.value)}
            onBlur={field.onBlur}
          />
          {showHint && <Hint id={hintId}>{hint}</Hint>}
          {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
        </div>
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormTextArea;
